import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Star, TrendingUp, TrendingDown, Minus, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

/**
 * Compact strip that shows starred tickers (from Sohel Pick watchlist)
 * alongside their live Sohel Pick action + confidence. Tapping a chip
 * deep-links into the Sohel Pick page.
 */
export default function StarredPlaysStrip() {
  const [items, setItems] = useState(null); // null = loading, [] = empty, [...] = loaded

  const fetchData = useCallback(async () => {
    try {
      const [wlRes, spRes] = await Promise.all([
        api.get("/sohel-pick/watchlist"),
        api.get("/sohel-pick")
      ]);
      const symbols = new Set((wlRes.data?.watchlist || [])
        .map((w) => (w.symbol || "").toUpperCase()));
      if (symbols.size === 0) {
        setItems([]);
        return;
      }
      // Pull analysis from all Sohel Pick buckets
      const all = [
        ...(spRes.data?.buy_calls || []),
        ...(spRes.data?.buy_puts || []),
        ...(spRes.data?.hold || []),
        ...(spRes.data?.unusual_activity || [])
      ];
      const byId = new Map();
      all.forEach((p) => {
        if (p?.symbol && symbols.has(p.symbol.toUpperCase())) {
          byId.set(p.symbol.toUpperCase(), p);
        }
      });
      // Preserve order of watchlist; fill with placeholder for ones we haven't scored yet
      const list = Array.from(symbols).map((sym) =>
        byId.get(sym) || { symbol: sym, action: "PENDING", confidence: 0 }
      );
      setItems(list);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 120000); // refresh every 2 min
    return () => clearInterval(t);
  }, [fetchData]);

  if (items === null) {
    return (
      <Card className="card-dark border-yellow-500/20 bg-yellow-500/5">
        <CardContent className="p-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Star className="w-4 h-4 text-yellow-400 animate-pulse" />
          Loading starred plays…
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="card-dark border-yellow-500/20 bg-yellow-500/5" data-testid="starred-plays-empty">
        <CardContent className="p-3 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Star className="w-4 h-4 text-yellow-400" />
            <span>No starred plays yet. Star any ticker on the Earnings page to pin it here.</span>
          </div>
          <Link
            to="/earnings"
            className="text-yellow-400 hover:text-yellow-300 font-bold flex items-center gap-1"
            data-testid="goto-earnings-link"
          >
            Go to Earnings <ChevronRight className="w-3 h-3" />
          </Link>
        </CardContent>
      </Card>
    );
  }

  const actionStyle = (action) => {
    if (action === "BUY CALLS") return { icon: TrendingUp, cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" };
    if (action === "BUY PUTS") return { icon: TrendingDown, cls: "text-red-400 border-red-500/40 bg-red-500/10" };
    if (action === "HOLD") return { icon: Minus, cls: "text-gray-400 border-gray-500/40 bg-gray-500/10" };
    return { icon: Star, cls: "text-yellow-400 border-yellow-500/40 bg-yellow-500/10" };
  };

  return (
    <Card className="card-dark border-yellow-500/20 bg-yellow-500/5" data-testid="starred-plays-strip">
      <CardContent className="p-3">
        <div className="flex items-center gap-3 overflow-x-auto scrollbar-thin pb-1">
          <div className="flex items-center gap-2 text-xs font-bold text-yellow-400 whitespace-nowrap">
            <Star className="w-4 h-4 fill-yellow-400" />
            STARRED PLAYS
            <span className="text-gray-500 font-normal">({items.length})</span>
          </div>
          {items.map((p) => {
            const { icon: Icon, cls } = actionStyle(p.action);
            const priceChg = typeof p.daily_change === "number" ? p.daily_change : null;
            return (
              <Link
                key={p.symbol}
                to="/sohel-pick"
                data-testid={`starred-chip-${p.symbol}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${cls} hover:scale-[1.03] transition-transform whitespace-nowrap`}
                title={`${p.symbol}: ${p.action}${p.confidence ? ` @ ${p.confidence}%` : ""}`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="text-sm font-bold text-white">{p.symbol}</span>
                {p.current_price ? (
                  <span className="text-xs font-data text-gray-300">${p.current_price}</span>
                ) : null}
                {priceChg !== null && (
                  <span className={`text-xs font-data ${priceChg >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {priceChg >= 0 ? "+" : ""}{priceChg.toFixed(1)}%
                  </span>
                )}
                {p.confidence > 0 && (
                  <Badge variant="outline" className="text-[10px] border-white/20 text-gray-300 px-1 py-0">
                    {p.confidence}%
                  </Badge>
                )}
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
